import {useMemo} from 'react';
import {Link as RouterLink} from 'react-router-dom';
import {Link, Rating} from '@mui/material';
import {MRT_ColumnDef} from 'material-react-table';

import {UserHoverCard} from '@entities/user';
import {MrTable} from '@shared/components';
import {DATETIME_FORMAT, entityTableCommonProps} from '@shared/constants';
import {useUrlState} from '@shared/hooks';
import {formatInTz} from '@shared/utils';

import {useGetRatings, UserRating} from '@/entities/rating';
import {ROUTE__USER_ACCOUNT} from '@/shared/constants';
import {PaginationState} from '@/shared/types';

const localStorageKey = 'ratesTable';

type UrlState = PaginationState;

const columns: MRT_ColumnDef<UserRating>[] = [
  {
    accessorKey: 'username',
    header: 'Username',
    Cell: ({row}) => (
      <UserHoverCard userId={row.original.userId}>
        <Link
          component={RouterLink}
          to={ROUTE__USER_ACCOUNT.replace(':id', row.original.userId)}
        >
          {row.original.username}
        </Link>
      </UserHoverCard>
    ),
    size: 160,
  },
  {
    accessorKey: 'rating',
    header: 'Rating',
    Cell: ({row}) => (
      <Rating value={row.original.rating} readOnly size="small" />
    ),
    size: 160,
  },
  {
    accessorKey: 'comment',
    header: 'Comment',
    size: 320,
  },
  {
    accessorKey: 'userAgent',
    header: 'User agent',
    size: 240,
  },
  {
    accessorKey: 'created',
    header: 'Created',
    Cell: ({row}) => formatInTz(row.original.created, DATETIME_FORMAT),
    size: 160,
  },
];

export const RatesPage = () => {
  const {urlState, setUrl} = useUrlState<UrlState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const {
    data,
    isPending: isLoading,
    error,
  } = useGetRatings({
    page: urlState.pageIndex + 1,
    pageSize: urlState.pageSize,
  });

  const ratings = useMemo<UserRating[]>(() => data?.items ?? [], [data?.items]);

  return (
    <MrTable
      error={error}
      columns={columns}
      data={ratings}
      enableColumnOrdering
      enableColumnDragging={false}
      localStorageKeyForSettings={localStorageKey}
      enableHiding
      /////////////////////////////
      // pagination + state
      enablePagination
      manualPagination
      onPaginationChange={setUrl}
      rowCount={data?.total ?? 0}
      /////////////////////////////
      state={{
        isLoading,
        pagination: urlState,
      }}
      enableColumnPinning
      initialState={{
        columnPinning: {
          left: ['username'],
        },
      }}
      {...entityTableCommonProps}
      enableRowActions={false}
    />
  );
};
